import { useEffect, useState } from "react";
import type { EmailRevertResponse } from "@starsach/shared-types";
import { postPublic } from "../api/client";
import { StarsachMark } from "../components/Brand";
import { ErrorState, btnPrimary } from "../components/ui";
import { loadConfig } from "../config";

/** The token rides in the fragment, which no browser sends to a server; it is read once and taken out of the address bar. */
function takeToken(): string {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("t") ?? "";
  if (token) window.history.replaceState(window.history.state, "", window.location.pathname);
  return token;
}

/**
 * #580: "This wasn't me", from the email sent to the old address of an
 * email change. No sign-in - that address may no longer have one. Opening
 * the page changes nothing; the button does (a GET never mutates).
 */
export function EmailRevertPage() {
  const [token] = useState(takeToken);
  const [state, setState] = useState<{ busy: boolean; result?: EmailRevertResponse; error?: unknown }>({ busy: false });
  useEffect(() => {
    document.title = "Undo an email change · Starsach";
  }, []);

  const undo = async () => {
    setState({ busy: true });
    try {
      const config = await loadConfig();
      setState({ busy: false, result: await postPublic<EmailRevertResponse>(config, "/v1/public/email-revert", { token }) });
    } catch (error) {
      setState({ busy: false, error });
    }
  };

  return (
    <main className="mx-auto max-w-lg px-4 py-12">
      <StarsachMark className="h-10 w-10 text-brand" aria-hidden="true" />
      <h1 className="mt-6 text-2xl font-semibold text-ink dark:text-white">Undo an email change</h1>
      <div className="mt-4 space-y-3 rounded-xl border border-neutral-200 bg-white p-5 text-sm dark:border-neutral-800 dark:bg-neutral-900">
        {state.result?.outcome === "reverted" ? (
          <div role="status" className="space-y-2">
            <p>Done. Your account is back on <span className="break-words font-medium">{state.result.email}</span>, and every session was signed out.</p>
            <p>We have sent a code to that address. Use it to choose a new password the next time you sign in; your old password no longer works.</p>
            <a className={btnPrimary} href="/login">Go to sign-in</a>
          </div>
        ) : state.result?.outcome === "canceled" ? (
          <div role="status" className="space-y-2">
            <p>Done. The change was stopped before it happened, so your email address is the same as before.</p>
            <p>If you did not ask for it, change your password the next time you sign in.</p>
            <a className={btnPrimary} href="/login">Go to sign-in</a>
          </div>
        ) : !token ? (
          <p>This link is incomplete. Open it again from the email. If someone else has your account, write to support@gairm.org.</p>
        ) : (
          <>
            <p>If you did not ask to change the email address on your Starsach account, undo it here. You do not need to sign in.</p>
            <p>If the change has already happened, this puts your address back, signs out every session (including the one that made the change), and asks you to choose a new password.</p>
            <button type="button" className={btnPrimary} disabled={state.busy} onClick={() => void undo()}>
              {state.busy ? "Undoing…" : "Undo the change"}
            </button>
            {state.error !== undefined && <ErrorState error={state.error} lead="Couldn’t undo it." />}
          </>
        )}
      </div>
    </main>
  );
}
